import { useState, useRef, useEffect } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { NotFoundException } from "@zxing/library";

interface AttendeeRecord {
  id: string;
  name: string;
  certNumber: string;
  timestamp: string;
  scanMethod: "barcode" | "manual";
}

export default function BarcodeScannerCard() {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedData, setScannedData] = useState<AttendeeRecord[]>([]);
  const [manualEntry, setManualEntry] = useState("");
  const [manualName, setManualName] = useState("");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastScan, setLastScan] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    codeReaderRef.current = new BrowserMultiFormatReader();
    
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    console.log("=== START SCANNING CLICKED ===");
    try {
      setCameraError(null);
      setIsScanning(true);
      console.log("State updated: isScanning = true");

      // Wait for DOM to update after state change
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!codeReaderRef.current || !videoRef.current) {
        console.error("Missing refs after DOM update:", { 
          codeReader: !!codeReaderRef.current, 
          video: !!videoRef.current,
          videoElement: videoRef.current
        });
        setCameraError("Video element not ready. Please try again.");
        setIsScanning(false);
        return;
      }

      console.log("Refs OK, checking getUserMedia support...");
      
      // Detailed browser support check
      console.log("Browser capabilities:", {
        navigator: !!navigator,
        mediaDevices: !!navigator.mediaDevices,
        getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
        isSecureContext: window.isSecureContext,
        protocol: window.location.protocol,
        host: window.location.host
      });

      if (!navigator.mediaDevices) {
        throw new Error("MediaDevices API not available. This usually means the site is not served over HTTPS.");
      }

      if (!navigator.mediaDevices.getUserMedia) {
        throw new Error("getUserMedia not supported in this browser");
      }

      // Check for HTTPS requirement
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        throw new Error("Camera access requires HTTPS or localhost. Current protocol: " + window.location.protocol);
      }

      // Get camera stream with better error handling
      console.log("Requesting camera access...");
      
      let stream;
      try {
        // Try back camera first (mobile)
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" } // Use back camera on mobile
        });
      } catch (error: any) {
        console.log("Back camera failed:", error.message);
        console.log("Trying any camera...");
        // Fallback to any available camera
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true
          });
        } catch (fallbackError: any) {
          console.error("All camera attempts failed:", fallbackError);
          throw new Error(`Camera access denied: ${fallbackError.message}. Make sure you've allowed camera permissions in your browser.`);
        }
      }
      
      console.log("Camera access granted");
      
      streamRef.current = stream;
      videoRef.current.srcObject = stream;

      // Start barcode detection
      console.log("🔍 Starting barcode detection...");
      codeReaderRef.current.decodeFromVideoDevice(
        undefined, // Use default camera
        videoRef.current,
        (result, error) => {
          if (result) {
            const scannedText = result.getText();
            console.log("📦 BARCODE DETECTED:", scannedText);
            console.log("📦 Barcode format:", result.getBarcodeFormat());
            
            // Avoid duplicate scans within 2 seconds
            if (scannedText !== lastScan) {
              console.log("✅ Adding new barcode scan to records");
              setLastScan(scannedText);
              addScannedRecord(scannedText, "barcode");
              
              // Show success feedback
              setCameraError(""); // Clear any error
              setSuccessMessage(`✅ Barcode scanned: ${scannedText}`);
              
              // Clear success message after 3 seconds
              setTimeout(() => setSuccessMessage(null), 3000);
              
              // Clear lastScan after 2 seconds to allow rescanning the same code
              setTimeout(() => setLastScan(null), 2000);
            } else {
              console.log("🔄 Duplicate scan ignored (within 2 seconds)");
            }
          }
          
          if (error && !(error instanceof NotFoundException)) {
            console.warn("⚠️ Barcode scan error:", error);
          }
        }
      );
    } catch (error: any) {
      console.error("Camera access error:", error);
      
      // Use the detailed error message if it's one of our custom errors
      if (error.message && (
        error.message.includes("MediaDevices API not available") ||
        error.message.includes("getUserMedia not supported") ||
        error.message.includes("Camera access requires HTTPS") ||
        error.message.includes("Camera access denied")
      )) {
        setCameraError(error.message);
      } else {
        // Handle DOMException errors from getUserMedia
        let errorMessage = "Unable to access camera. ";
        
        const mediaError = error as DOMException;
        if (mediaError.name === "NotAllowedError") {
          errorMessage += "Permission denied. Please allow camera access in your browser settings.";
        } else if (mediaError.name === "NotFoundError") {
          errorMessage += "No camera found on this device.";
        } else if (mediaError.name === "NotSupportedError") {
          errorMessage += "Camera access not supported over insecure connection. Try using HTTPS.";
        } else {
          errorMessage += mediaError.message || "Please check camera permissions.";
        }
        
        setCameraError(errorMessage);
      }
      
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    setIsScanning(false);
    
    // Stop video stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Reset video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    // Create new code reader instance if needed
    if (!codeReaderRef.current) {
      codeReaderRef.current = new BrowserMultiFormatReader();
    }
  };

  const addScannedRecord = (certNumber: string, method: "barcode" | "manual", name?: string) => {
    const newRecord: AttendeeRecord = {
      id: Date.now().toString(),
      name: name || `Attendee ${scannedData.length + 1}`,
      certNumber: certNumber.trim(),
      timestamp: new Date().toLocaleString(),
      scanMethod: method
    };

    setScannedData(prev => [...prev, newRecord]);
  };

  const addManualEntry = () => {
    if (!manualEntry.trim()) return;
    
    addScannedRecord(manualEntry, "manual", manualName || undefined);
    setManualEntry("");
    setManualName("");
  };

  const removeRecord = (id: string) => {
    setScannedData(prev => prev.filter(record => record.id !== id));
  };

  const exportToCSV = () => {
    if (scannedData.length === 0) {
      alert("No data to export!");
      return;
    }

    // Create CSV headers matching CE program requirements
    const headers = [
      "Name",
      "Certification Number", 
      "Scan Time",
      "Scan Method"
    ];

    // Convert data to CSV format
    const csvContent = [
      headers.join(","),
      ...scannedData.map(record => [
        `"${record.name}"`,
        `"${record.certNumber}"`,
        `"${record.timestamp}"`,
        `"${record.scanMethod}"`
      ].join(","))
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    
    const filename = `CE_Attendance_${new Date().toISOString().split('T')[0]}.csv`;
    link.href = url;
    link.download = filename;
    link.click();
    
    URL.revokeObjectURL(url);
  };

  const clearAllData = () => {
    if (window.confirm("Are you sure you want to clear all scanned data?")) {
      setScannedData([]);
    }
  };

  return (
    <div className="bg-white shadow-xl rounded-xl p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
        📱 Barcode Scanner for CE Program
      </h2>

      {/* Camera Section */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 mb-4">
          {!isScanning ? (
            <button
              onClick={() => {
                console.log("Button clicked!");
                startScanning();
              }}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              📹 Start Camera
            </button>
          ) : (
            <button
              onClick={stopScanning}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              ⏹️ Stop Camera
            </button>
          )}
        </div>

        {/* Video Preview */}
        {isScanning && (
          <div className="mb-4">
            <video
              ref={videoRef}
              className="w-full max-w-md rounded-lg border-2 border-gray-300"
              autoPlay
              playsInline
              muted
            />
            <p className="text-sm text-gray-600 mt-2">
              📸 Point camera at barcode on certification card
            </p>
          </div>
        )}

        {/* Camera Error */}
        {cameraError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong>Camera Error:</strong> {cameraError}
            <br />
            <small>Make sure you've allowed camera permissions in your browser.</small>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}
      </div>

      {/* Manual Entry Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">✏️ Manual Entry</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="Attendee Name (optional)"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            className="px-3 py-2 border rounded"
          />
          <input
            type="text"
            placeholder="Certification Number"
            value={manualEntry}
            onChange={(e) => setManualEntry(e.target.value)}
            className="px-3 py-2 border rounded"
          />
          <button
            onClick={addManualEntry}
            disabled={!manualEntry.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
          >
            ➕ Add Entry
          </button>
        </div>
      </div>

      {/* Scanned Data Table */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            📋 Scanned Attendance ({scannedData.length} records)
          </h3>
          <div className="flex gap-2">
            <button
              onClick={exportToCSV}
              disabled={scannedData.length === 0}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-300 transition-colors"
            >
              📥 Export CSV
            </button>
            <button
              onClick={clearAllData}
              disabled={scannedData.length === 0}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-300 transition-colors"
            >
              🗑️ Clear All
            </button>
          </div>
        </div>

        {scannedData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Cert Number</th>
                  <th className="px-4 py-2 text-left">Time</th>
                  <th className="px-4 py-2 text-left">Method</th>
                  <th className="px-4 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {scannedData.map((record, index) => (
                  <tr key={record.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-4 py-2">{record.name}</td>
                    <td className="px-4 py-2 font-mono text-sm">{record.certNumber}</td>
                    <td className="px-4 py-2 text-sm">{record.timestamp}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        record.scanMethod === 'barcode' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {record.scanMethod === 'barcode' ? '📱 Scanned' : '✏️ Manual'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => removeRecord(record.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        🗑️ Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>📋 No attendance records yet.</p>
            <p className="text-sm">Start scanning barcodes or add entries manually.</p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">📖 Instructions:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Click "Start Camera" and point at certification card barcodes</li>
          <li>• The app works with most standard barcode formats (Code 128, Code 39, QR codes)</li>
          <li>• For cards that won't scan, use manual entry</li>
          <li>• Export to CSV when done for CDPH reporting</li>
          <li>• Works on mobile devices and tablets</li>
        </ul>
      </div>
    </div>
  );
}
